// According to https://docs.sentry.io/platforms/javascript/guides/react/
// Sentry initialization should be imported first
// eslint-disable-next-line simple-import-sort/imports
import initSentry from "log/monitoring/init";

import "./assets/styles/css/bootstrap.css";
import "./assets/styles/css/app-fonts.css";

import { ConnectedRouter } from "connected-react-router";
import { CookiesProvider } from "react-cookie";
import { render } from "react-dom";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { ThemeProvider, StyledEngineProvider } from "@mui/material";
import { WEB_LOGIN_MODE } from "fieldpro-tools";

import Loader from "components/Progress/CustomCircularProgress";
import { AppTheme } from "containers/app/AppTheme";
import Root from "containers/app/Root";
import { ssoLoginAction } from "containers/authentication/redux/actions";
import configureStore from "redux/store/configureStore";
import { history } from "redux/store/history";
import { msalConfig } from "auth/msalConfig";

const { store, persistor } = configureStore();

const msalInstance = new PublicClientApplication(msalConfig);

// assign the target node or create a div element for testing purposes
const domTarget =
  document.getElementById("root") || document.createElement("div");

function renderApp() {
  render(
    <Provider store={store}>
      <MsalProvider instance={msalInstance}>
        <PersistGate
          loading={
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={AppTheme}>
                <Loader />
              </ThemeProvider>
            </StyledEngineProvider>
          }
          persistor={persistor}
        >
          <ConnectedRouter history={history}>
            <CookiesProvider>
              <Root />
            </CookiesProvider>
          </ConnectedRouter>
        </PersistGate>
      </MsalProvider>
    </Provider>,
    domTarget,
  );
}

msalInstance
  .initialize()
  .then(async () => {
    try {
      const response = await msalInstance.handleRedirectPromise();

      if (response) {
        msalInstance.setActiveAccount(response.account);

        const token = response.idToken || response.accessToken;
        if (token) {
          store.dispatch(ssoLoginAction(token, WEB_LOGIN_MODE.MICROSOFT_SSO));
        }
      }
    } catch (error) {
      if (error?.errorCode === "hash_empty_error") {
        console.warn(
          "MSAL hash_empty_error - this is normal if no redirect occurred",
        );
        if (window.location.hash && !window.location.hash.includes("code=")) {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname + window.location.search,
          );
        }
      } else if (error?.errorCode === "unauthorized_client") {
        console.error(
          "MSAL unauthorized_client - check redirect URI in Azure AD:",
          error?.message,
        );
        console.error(
          "Configured redirect URI should be:",
          window.location.origin,
        );
        console.error(
          "Make sure this exact URL is registered in Azure AD redirect URIs",
        );
      } else {
        console.error("MSAL handleRedirectPromise error:", error);
      }
    }

    if (
      !msalInstance.getActiveAccount() &&
      msalInstance.getAllAccounts().length > 0
    ) {
      msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    }

    msalInstance.addEventCallback((event) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS &&
        event.payload.account
      ) {
        setTimeout(() => {
          msalInstance.setActiveAccount(event.payload.account);
        }, 100);
      }
    });

    initSentry();

    renderApp();
  })
  .catch((initError) => {
    console.error("MSAL initialization failed:", initError);
    initSentry();
    renderApp();
  });
